import { db } from "services/firebase";
import firebase from "firebase/compat/app";

/** Pilot TCIs preferred as the happy-path focus track. */
export const HAPPY_PATH_PILOT_TCIS = ["triage", "differential", "medrec-intake"];

export const HAPPY_PATH_STEP_IDS = [
  "institution",
  "domain",
  "tciManager",
  "governance",
  "fdeC",
  "session",
  "annotation",
  "candidate",
  "loopHealth",
];

/**
 * Pure derivation of the 9 happy-path steps from already-loaded snapshots.
 * @param {object} input
 */
export function deriveTciHappyPathSteps({
  organization = null,
  domains = [],
  fdeAssignments = [],
  governanceProfiles = [],
  fdeCUsers = {},
  sessions = [],
  annotations = [],
  candidates = [],
  loopHealthRows = [],
} = {}) {
  const activeDomains = (domains || []).filter((d) => d && d.isActive !== false);
  const domainWithTcis = activeDomains.find(
    (d) => Array.isArray(d.tciIds) && d.tciIds.length > 0,
  );
  const allTciIds = [
    ...new Set(activeDomains.flatMap((d) => (Array.isArray(d.tciIds) ? d.tciIds : []))),
  ];
  const focusTciId =
    HAPPY_PATH_PILOT_TCIS.find((id) => allTciIds.includes(id)) || allTciIds[0] || null;

  const activeDc = (fdeAssignments || []).some(
    (a) => a.role === "fde-dc" && a.status === "active",
  );
  const domainHasManager = activeDomains.some((d) => Boolean(d.tciManagerUid));

  const activeProfiles = (governanceProfiles || []).filter(
    (p) =>
      p &&
      (p.status === "active" ||
        (p.passesCompleted?.pass1 &&
          p.passesCompleted?.pass2 &&
          p.passesCompleted?.pass3)),
  );
  const focusProfile =
    activeProfiles.find((p) => p.tciId === focusTciId) || activeProfiles[0] || null;

  const activeFdeCs = (fdeAssignments || []).filter(
    (a) => a.role === "fde-c" && a.status === "active",
  );
  const fdeCReady = activeFdeCs.some((a) => {
    const user = fdeCUsers[a.uid];
    if (!user) return a.trainingComplete === true;
    return user.roleOnboarding?.fdeCComplete === true || a.trainingComplete === true;
  });

  const sessionsWithTci = (sessions || []).filter((s) => Boolean(s.tciId));
  const submittedAnnotations = (annotations || []).filter(
    (a) => a.status === "submitted" || a.status === "consolidated",
  );
  const reviewedCandidates = (candidates || []).filter((c) =>
    ["approved", "rejected", "returned"].includes(c.status),
  );
  const pendingCandidates = (candidates || []).filter((c) => c.status === "pending");
  const loopLive = (loopHealthRows || []).some(
    (row) =>
      row &&
      (row.loopStatus ||
        row.weeklyAnnotationCount > 0 ||
        row.daysSinceLastAnnotation != null ||
        row.daysSinceLastPromotion != null),
  );

  const govHref = focusTciId
    ? focusProfile?.status === "active"
      ? `/dashboard/tci-governance/${encodeURIComponent(focusTciId)}`
      : `/dashboard/tci-governance/new?tciId=${encodeURIComponent(focusTciId)}`
    : "/dashboard/tci-governance/new";

  const pendingCandidateHref = pendingCandidates[0]?.id
    ? `/dashboard/tci-manager/candidates/${pendingCandidates[0].id}`
    : "/dashboard/tci-manager";

  const annotationSessionId =
    submittedAnnotations[0]?.sessionId || sessionsWithTci[0]?.id || null;

  const steps = [
    {
      id: "institution",
      index: 1,
      title: "Institution setup",
      description: "Complete your institution profile so governance can attach to an org.",
      done: Boolean(organization?.setupCompletedAt),
      href: "/dashboard/setup/institution",
      owner: "admin",
    },
    {
      id: "domain",
      index: 2,
      title: "Clinical domain + TCIs",
      description: "Create an active domain and attach the TCIs you will govern.",
      done: Boolean(domainWithTcis),
      href: "/dashboard/domains",
      owner: "admin",
      meta: { tciCount: allTciIds.length, focusTciId },
    },
    {
      id: "tciManager",
      index: 3,
      title: "TCI Manager assigned",
      description: "Assign a TCI Manager for the domain.",
      done: activeDc || domainHasManager,
      href: "/dashboard/fde-roster",
      owner: "admin",
    },
    {
      id: "governance",
      index: 4,
      title: "Governance active",
      description: "Finish the interrogatory so at least one TCI profile is active.",
      done: activeProfiles.length > 0,
      href: govHref,
      owner: "admin",
      meta: { focusTciId },
    },
    {
      id: "fdeC",
      index: 5,
      title: "Clinical Annotator ready",
      description: "Assign a Clinical Annotator and complete orientation.",
      done: fdeCReady,
      href: "/dashboard/fde-roster",
      secondaryHref: focusTciId
        ? `/dashboard/training-decks/${encodeURIComponent(focusTciId)}`
        : null,
      owner: "admin",
    },
    {
      id: "session",
      index: 6,
      title: "Session captured",
      description: "Capture at least one conversation session tagged with a TCI.",
      done: sessionsWithTci.length > 0,
      href: "/dashboard/sessions",
      owner: "fde-c",
    },
    {
      id: "annotation",
      index: 7,
      title: "Annotation submitted",
      description: "Submit teaching feedback on a session for the governance loop.",
      done: submittedAnnotations.length > 0,
      href: annotationSessionId
        ? `/dashboard/sessions/${annotationSessionId}`
        : "/dashboard/sessions",
      owner: "fde-c",
    },
    {
      id: "candidate",
      index: 8,
      title: "Candidate reviewed",
      description: "Promote, reject, or return at least one prompt candidate.",
      done: reviewedCandidates.length > 0,
      href: pendingCandidates.length ? pendingCandidateHref : "/dashboard/tci-manager",
      owner: "fde-dc",
      meta: {
        pendingCount: pendingCandidates.length,
        reviewedCount: reviewedCandidates.length,
      },
    },
    {
      id: "loopHealth",
      index: 9,
      title: "Loop health live",
      description: "Confirm loop health metrics are recording activity for your TCIs.",
      done: loopLive || (reviewedCandidates.length > 0 && submittedAnnotations.length > 0),
      href: "/dashboard/tci-manager",
      owner: "fde-dc",
    },
  ];

  const completedCount = steps.filter((s) => s.done).length;
  const nextStep = steps.find((s) => !s.done) || null;
  const setupComplete = steps.slice(0, 5).every((s) => s.done);
  const allComplete = completedCount === steps.length;

  return {
    steps,
    completedCount,
    totalCount: steps.length,
    nextStep,
    setupComplete,
    allComplete,
    focusTciId,
    domainTciCount: allTciIds.length,
  };
}

/**
 * Load institution snapshots and derive happy-path progress.
 */
export async function loadTciHappyPathProgress(institutionId) {
  if (!institutionId) {
    return deriveTciHappyPathSteps();
  }

  const [
    orgSnap,
    domainsSnap,
    assignmentsSnap,
    profilesSnap,
    sessionsSnap,
    annotationsSnap,
    candidatesSnap,
    loopSnap,
  ] = await Promise.all([
    db.collection("organizations").doc(institutionId).get(),
    db.collection("clinicalDomains").where("institutionId", "==", institutionId).get(),
    db.collection("fdeAssignments").where("institutionId", "==", institutionId).get(),
    db
      .collection("tciGovernanceProfiles")
      .where("institutionId", "==", institutionId)
      .get(),
    db
      .collection("conversationSessions")
      .where("institutionId", "==", institutionId)
      .limit(50)
      .get(),
    db
      .collection("teacherAnnotations")
      .where("institutionId", "==", institutionId)
      .limit(50)
      .get(),
    db
      .collection("promptCandidates")
      .where("institutionId", "==", institutionId)
      .limit(50)
      .get(),
    db
      .collection("loopHealthMetrics")
      .where("institutionId", "==", institutionId)
      .limit(20)
      .get(),
  ]);

  const domains = domainsSnap.docs.map((d) => ({ id: d.id, ...d.data() }));
  const fdeAssignments = assignmentsSnap.docs.map((d) => ({ id: d.id, ...d.data() }));
  const fdeCUids = [
    ...new Set(
      fdeAssignments
        .filter((a) => a.role === "fde-c" && a.status === "active")
        .map((a) => a.uid)
        .filter(Boolean),
    ),
  ];

  const fdeCUsers = {};
  await Promise.all(
    fdeCUids.map(async (uid) => {
      try {
        const snap = await db.collection("users").doc(uid).get();
        if (snap.exists) fdeCUsers[uid] = snap.data();
      } catch {
        // ignore missing users
      }
    }),
  );

  return deriveTciHappyPathSteps({
    organization: orgSnap.exists ? { id: orgSnap.id, ...orgSnap.data() } : null,
    domains,
    fdeAssignments,
    governanceProfiles: profilesSnap.docs.map((d) => ({ id: d.id, ...d.data() })),
    fdeCUsers,
    sessions: sessionsSnap.docs.map((d) => ({ id: d.id, ...d.data() })),
    annotations: annotationsSnap.docs.map((d) => ({ id: d.id, ...d.data() })),
    candidates: candidatesSnap.docs.map((d) => ({ id: d.id, ...d.data() })),
    loopHealthRows: loopSnap.docs.map((d) => ({ id: d.id, ...d.data() })),
  });
}

export async function setTciHappyPathChecklistHidden(hidden) {
  const user = firebase.auth().currentUser;
  if (!user?.uid) {
    throw new Error("You must be signed in to update checklist preferences.");
  }
  await db.collection("users").doc(user.uid).update({
    hideTciHappyPathChecklist: Boolean(hidden),
    updatedAt: firebase.firestore.FieldValue.serverTimestamp(),
  });
}

export async function loadTciPilotDemo() {
  const callable = firebase.app().functions("us-central1").httpsCallable("loadTciPilotDemo");
  const result = await callable({});
  return result?.data || null;
}
